import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent, updateMostRecentJournalEntryPlan, getCompletedCommitmentsCount, calculateRefreshRate, calculateEnhancedOptimalPace, updateUserProgress } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px);
  position: relative;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px);
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px);
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 8px;
`;

const NoDataMessage = styled.div`
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const NoDataTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const NoDataText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

// Bar Visualization Components (matching Optimal Pace Race style)
const BarContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.md} auto ${theme.spacing.lg} auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  /* Allow scrolling on mobile */
  @media (max-width: ${theme.breakpoints.tablet}) {
    touch-action: manipulation;
  }
`;

const BarWrapper = styled.div`
  width: 80vw;
  max-width: 80vw;
  height: 40px;
  position: relative;
  background: #B8C5D1;
  border-radius: ${theme.borderRadius.lg};
  overflow: visible;
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw;
    max-width: 80vw;
  }
`;

const ScoreIndicator = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background: ${theme.colors.text};
  border: 3px solid white;
  border-radius: 50%;
  z-index: 10;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
`;

const ScoreLabel = styled.div`
  position: absolute;
  top: -43px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.text};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  white-space: nowrap;
  z-index: 11;
  box-shadow: ${theme.shadows.sm};
  
  /* Arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.text};
  }
`;

const BarLabels = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);

  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.doryDarkerBlue};
  font-weight: ${theme.typography.weights.medium};
  pointer-events: none;
  z-index: 5;
`;

const BarLabel = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  min-width: 20px;
`;

const LeftBarLabel = styled(BarLabel)`
  left: 10px;
`;

const RightBarLabel = styled(BarLabel)`
  right: 15px;
`;

// Number container with +/- buttons
const NumberContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${theme.spacing.md} 0 0 0;
  width: 100%;
`;

// Wrapper for number and label
const NumberWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

// Plus/Minus buttons
const AdjustButton = styled.button`
  position: absolute;
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  -webkit-tap-highlight-color: transparent;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  z-index: 1;
  
  &:hover {
    background: ${theme.colors.neutral}30;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    width: 48px;
    height: 48px;
    font-size: 2rem;
  }
`;

const DecrementButton = styled(AdjustButton)`
  left: calc(50% - 120px);
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    left: calc(50% - 100px);
  }
`;

const IncrementButton = styled(AdjustButton)`
  right: calc(50% - 120px);
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    right: calc(50% - 100px);
  }
`;

// Large Optimal Pace Number Display
const OptimalPaceNumber = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 11.2rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${props => props.$color || theme.colors.primary};
  margin: 0;
  text-align: center;
  line-height: 1;
  cursor: ${props => props.$isEditable ? 'pointer' : 'default'};
  user-select: none;
  transition: color ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  
  /* Responsive sizing */
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 8.4rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: 5.6rem;
  }
`;

// Optimal Pace label shown below number when at optimal pace
const OptimalPaceLabel = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: #435F60;
  text-align: center;
  line-height: 1.2;
  user-select: none;
  white-space: nowrap;
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.small};
  }
`;

// Optimal Pace icon
// Icon base styles - dimensions calculated to match actual SVG graphic content height
// Target visual height: 86px (20% larger than 72px) for actual graphic content (not viewBox)
// Optimal Pace: 20% larger - height 86px, width 130px (108 * 1.2)
const OptimalPaceIcon = styled.img`
  display: block;
  margin: ${theme.spacing.lg} auto 0 auto;
  width: 130px;
  height: 86px;
  object-fit: contain;
  object-position: center;
`;

// Capacity icon - viewBox 51x35, 20% larger: 86px height, width = 86 * (51/35) = ~126px
const CapacityIcon = styled.img`
  display: block;
  margin: ${theme.spacing.lg} auto 0 auto;
  width: 126px;
  height: 86px;
  object-fit: contain;
  object-position: center;
`;

// Performance icon - viewBox 49x40, 20% larger: 86px height, width = 86 * (49/40) = ~106px
const PerformanceIcon = styled.img`
  display: block;
  margin: ${theme.spacing.lg} auto 0 auto;
  width: 106px;
  height: 86px;
  object-fit: contain;
  object-position: center;
`;

// Gift (Deep Dive) icon - viewBox 45x42, 20% larger: 86px height, width = 86 * (45/42) = ~92px
const GiftIcon = styled.img`
  display: block;
  margin: ${theme.spacing.lg} auto 0 auto;
  width: 92px;
  height: 86px;
  object-fit: contain;
  object-position: center;
`;

// Deep Dive code display (bold, smaller text for mobile)
const DeepDiveCode = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  margin: 0;
  letter-spacing: 2px;
  
  /* Smaller on mobile */
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.h3};
    letter-spacing: 1px;
  }
`;

// Deep Dive instructions text
const DeepDiveInstructions = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
  margin: 0;
  width: 100%;
  line-height: ${theme.typography.lineHeights.normal};
`;

// Deep Dive message text
const DeepDiveMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  text-align: center;
  margin: ${theme.spacing.md} 0;
  width: 100%;
  max-width: 400px;
  text-wrap: balance;
`;

// Capacity score number (same style as Optimal Pace number)
const CapacityScore = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 11.2rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin: 0;
  text-align: center;
  line-height: 1;
  
  /* Responsive sizing */
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 8.4rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: 5.6rem;
  }
`;

// Capacity message text
const CapacityMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  text-align: center;
  margin: ${theme.spacing.md} 0;
  width: 100%;
  max-width: 400px;
  text-wrap: balance;
`;

// Performance score number (same style as Optimal Pace number)
const PerformanceScore = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 11.2rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin: 0;
  text-align: center;
  line-height: 1;
  
  /* Responsive sizing */
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 8.4rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: 5.6rem;
  }
`;

// Performance message text
const PerformanceMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  text-align: center;
  margin: ${theme.spacing.md} 0;
  width: 100%;
  max-width: 400px;
  text-wrap: balance;
`;

// Next button for Performance screen
const NextButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  margin-top: ${theme.spacing.lg};
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  padding: 14px 28px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Optimal Pace heading
const OptimalPaceHeading = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: calc(${theme.typography.sizes.h2} * .9);
  font-weight: ${theme.typography.weights.semiBold};
  color: #435F60;
  text-align: center;
  margin: calc(${theme.spacing.sm} * 0) 0 0 0;
  width: 100%;
`;

// Question/Confirmation text
const QuestionText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  text-align: center;
  margin: 40px 0 0 0;
  width: 100%;
  max-width: 400px;
  text-wrap: balance;
`;

// Recovery Plan icon - 20% larger: height 86px, width 109px (91 * 1.2)
const RecoveryPlanIcon = styled.img`
  display: block;
  margin: ${theme.spacing.lg} auto 0 auto;
  width: 109px;
  height: 86px;
  object-fit: contain;
  object-position: center;
  cursor: pointer;
  transition: transform ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Compact Optimal Pace box (shown after Design My Day button is clicked)
const CompactOptimalPaceBox = styled.div`
  background: ${theme.colors.accent};
  border: 2px solid ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: calc(${theme.typography.sizes.body} * 2);
  color: ${theme.colors.doryBeige};
  text-align: center;
  width: 100%;
  max-width: 100%;
  margin: 0;
  align-self: stretch;
  box-shadow: ${theme.shadows.sm};
`;


// Energy Balance Visualization Components
const BalanceContainer = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0;
  padding: ${theme.spacing.lg} ${theme.spacing.lg} 0 ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
`;

const BalanceScale = styled.div`
  width: 100%;
  max-width: 400px;
  height: 120px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${theme.spacing.md} 0 0 0;
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    height: 100px;
    max-width: 320px;
  }
`;

const ScaleBase = styled.div`
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 40px;
  background: ${props => props.$baseColor || theme.colors.text};
  border-radius: ${theme.borderRadius.sm};
  transition: background ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    top: 19px;
    height: 35px;
  }
`;

const ScaleBar = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%) ${props => props.$isBalanced ? 'rotate(0deg)' : `rotate(${props.$tilt}deg)`};
  width: ${props => props.$barWidth || '280px'};
  height: 4px;
  background: ${props => props.$barColor || theme.colors.text};
  border-radius: ${theme.borderRadius.sm};
  transition: transform ${theme.animation.durations.normal} ${theme.animation.easings.easeOut}, 
              background ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    width: ${props => props.$barWidthMobile || '240px'};
    top: 15px;
  }
`;

const BalanceSide = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  width: 120px;
  transform: translateY(-50%);
  transition: top ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    width: 100px;
  }
`;

const LeftSide = styled(BalanceSide)`
  left: calc(50% - 140px);
  top: ${props => props.$verticalOffset ? `calc(22px + ${props.$verticalOffset}px)` : '22px'};
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    left: calc(50% - 120px);
    top: ${props => props.$verticalOffset ? `calc(17px + ${props.$verticalOffsetMobile || props.$verticalOffset}px)` : '17px'};
  }
`;

const RightSide = styled(BalanceSide)`
  right: calc(50% - 140px);
  top: ${props => props.$verticalOffset ? `calc(22px + ${props.$verticalOffset}px)` : '22px'};
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    right: calc(50% - 120px);
    top: ${props => props.$verticalOffset ? `calc(17px + ${props.$verticalOffsetMobile || props.$verticalOffset}px)` : '17px'};
  }
`;

const BalanceWeight = styled.div`
  width: ${props => props.$size || '60px'};
  height: ${props => props.$size || '60px'};
  border-radius: ${theme.borderRadius.full};
  background: ${props => props.$color || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.typography.headerFont};
  font-size: ${props => props.$fontSize || '1.5rem'};
  font-weight: ${theme.typography.weights.bold};
  color: white;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    width: ${props => props.$sizeMobile || '50px'};
    height: ${props => props.$sizeMobile || '50px'};
    font-size: ${props => props.$fontSizeMobile || '1.25rem'};
  }
`;

const BalanceLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
  font-weight: ${theme.typography.weights.medium};
`;

const RestorationCounter = styled.div`
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.accent}20;
  border: 2px solid ${theme.colors.accent};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  width: 100%;
  max-width: 300px;
`;

const RestorationLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
`;

const RestorationPoints = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.accent};
  text-align: center;
`;

// 24-Hour Day Visualization Components
const DayBarContainer = styled.div`
  width: 100%;
  max-width: 500px;
  margin: -10px 0 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const DayBarWrapper = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  box-shadow: ${theme.shadows.sm};
  background: ${theme.colors.neutral}30;
`;

const DayBarSegment = styled.div`
  height: 100%;
  background: ${props => props.$color || theme.colors.neutral};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  min-width: ${props => props.$percentage > 0 ? '2px' : '0'};
  position: relative;
  overflow: visible;
  
  ${props => props.$percentage === 0 && 'display: none;'}
`;

const DayBarSegmentText = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.semiBold};
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  z-index: 1;
`;

const DayBarLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
  font-weight: ${theme.typography.weights.medium};
  margin-bottom: 15px;
`;

const DayBarLabels = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.xs};
`;

const DayBarKey = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.lg};
  margin-top: 15px;
  flex-wrap: wrap;
`;

const DayBarKeyItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
`;

const DayBarKeyColor = styled.div`
  width: 16px;
  height: 16px;
  border-radius: ${theme.borderRadius.sm};
  background: ${props => props.$color || theme.colors.neutral};
  flex-shrink: 0;
`;

// Instruction text
const InstructionText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin: 0;
  text-align: center;
  width: 100%;
`;

// Total points earned display
const TotalPointsEarned = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  text-align: center;
  margin-top: 0;
  margin-bottom: ${theme.spacing.lg};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
`;

// Progress bar container
const ProgressBarContainer = styled.div`
  width: 100%;
  max-width: 400px;
  height: 24px;
  background: ${theme.colors.neutral}30;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
`;

// Progress bar fill
const ProgressBarFill = styled.div`
  height: 100%;
  width: ${props => props.$percentage || 0}%;
  background: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  transition: width ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

// Hour selector container
const HourSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.lg};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
`;

// Container for both selectors side by side
const SelectorsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xl};
  margin: 0;
  width: 100%;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    gap: ${theme.spacing.lg};
  }
`;

// Individual selector container (for hours or points)
const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex: 1;
  min-width: 150px;
  max-width: 250px;
`;

const SelectorLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
  text-align: center;
  margin-bottom: ${theme.spacing.xs};
`;

const SelectorControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.lg};
`;

// Large hour number display
const HourNumber = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 4rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${props => props.$color || theme.colors.accent};
  margin: 0;
  text-align: center;
  line-height: 1;
  min-width: 50px;
  
  /* Responsive sizing */
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 3rem;
    min-width: 37.5px;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: 2rem;
    min-width: 25px;
  }
`;

// Button base styles
const HourButton = styled.button`
  background: ${props => props.$backgroundColor || theme.colors.accent};
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  min-width: 60px;
  min-height: 60px;
  font-size: 2rem;
  font-weight: ${theme.typography.weights.bold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  box-shadow: ${theme.shadows.sm};
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
  
  &:hover {
    background: ${props => props.$hoverColor || theme.colors.doryGreen};
    transform: scale(1.05);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: scale(0.95);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 50px;
    height: 50px;
    min-width: 50px;
    min-height: 50px;
    font-size: 1.5rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    font-size: 1.25rem;
  }
`;

const DesignMyDayButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  margin: ${theme.spacing.lg} 0;
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  padding: 14px 28px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  padding: 14px 28px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LockInButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  margin: 15px 10px 10px 10px;
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  padding: 14px 28px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const PlanSummaryWrapper = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  align-self: stretch;
`;

const PlanSummaryBox = styled.div`
  background: ${theme.colors.doryLighterBlue};
  border: 2px solid ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  font-family: ${theme.typography.bodyFont};
  font-size: calc(${theme.typography.sizes.body} * 2 * 0.7);
  color: ${theme.colors.doryBeige};
  text-align: center;
  width: 100%;
  max-width: 100%;
  box-shadow: ${theme.shadows.sm};
`;

const ChangePlanLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
  transition: opacity ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    opacity: 0.6;
  }
`;

const RefreshLink = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-decoration: underline;
  cursor: pointer;
  transition: opacity ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  margin-left: ${theme.spacing.xs};
  vertical-align: middle;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    opacity: 0.6;
  }
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.sm};
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid ${theme.colors.textLight}30;
  border-top-color: ${theme.colors.textLight};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-left: ${theme.spacing.xs};
  vertical-align: middle;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -35px;
  right: -5px;
  background: transparent;
  border: none;
  color: ${theme.colors.textLight};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.bold};
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  transition: color ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    color: ${theme.colors.text};
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

// Info icon (top right corner) - gem icon
const InfoIcon = styled.img`
  position: absolute;
  top: calc(${theme.spacing.screenPadding} + 24px);
  right: ${theme.spacing.screenPadding};
  cursor: pointer;
  width: 32px; /* Proportional width based on SVG aspect ratio (90.3/78.85) */
  height: 28px;
  object-fit: contain;
  object-position: center;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  -webkit-tap-highlight-color: transparent;
  z-index: 10;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 32px;
    height: 28px;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    width: 32px;
    height: 28px;
  }
  
  &:hover {
    transform: scale(1.1);
    opacity: 0.9;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Overlay container (full screen)
const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.screenPadding};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

// Overlay content
const OverlayContent = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: ${theme.shadows.lg};
`;

// Overlay close button (X in top right)
const OverlayCloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: transparent;
  border: none;
  color: ${theme.colors.textLight};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.bold};
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  transition: color ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  -webkit-tap-highlight-color: transparent;
  border-radius: 50%;
  
  &:hover {
    color: ${theme.colors.text};
    background: ${theme.colors.neutral}20;
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

// Overlay item (icon + heading + message)
const OverlayItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Overlay icon
const OverlayIcon = styled.img`
  width: 92px;
  height: 86px;
  object-fit: contain;
  object-position: center;
  margin-bottom: ${theme.spacing.xs};
`;

// Overlay heading (icon name)
const OverlayHeading = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  text-align: center;
  margin: 0;
`;

// Overlay message
const OverlayMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  text-align: center;
  margin: 0;
`;

// Recovery Plan Display Mode Toggle
const ToggleBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${theme.spacing.md} 0;
  width: 100%;
  max-width: 400px;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  border: 2px solid ${theme.colors.neutral}40;
  background: ${theme.colors.neutral}20;
`;

const ToggleOption = styled.button`
  flex: 1;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: #3B515D;
  background: ${props => props.$isActive ? '#889b9c' : 'transparent'};
  border: none;
  border-right: 1px solid ${theme.colors.neutral}40;
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: ${props => props.$isActive ? '#435F60' : theme.colors.neutral}30;
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  &:last-child {
    border-right: none;
  }
`;

// Power display (large number)
const PowerDisplay = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 8rem;
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  text-align: center;
  margin: ${theme.spacing.md} 0;
  line-height: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 6rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: 4rem;
  }
`;

// Plays list container
const PlaysListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin: ${theme.spacing.md} 0;
  width: 100%;
`;

// Individual play item
const PlayItem = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  text-align: center;
  font-weight: normal;
`;

const OptimalPaceSoloScreen = ({ data, onNext, userProgress = {} }) => {
  const [optimalPaceData, setOptimalPaceData] = useState(userProgress.calculatedMetrics?.optimalPace);
  const [selectedHours, setSelectedHours] = useState(null);
  const [selectedPoints, setSelectedPoints] = useState(null);
  const [showDesignInterface, setShowDesignInterface] = useState(false);
  const [isPlanLocked, setIsPlanLocked] = useState(false);
  const [completedCommitmentsCount, setCompletedCommitmentsCount] = useState(null);
  const [isRefreshingPoints, setIsRefreshingPoints] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [adjustedHours, setAdjustedHours] = useState(null);
  const [showQuestion, setShowQuestion] = useState(true);
  const [isRecoveryPlan, setIsRecoveryPlan] = useState(false);
  const [showRecoveryPlanDetails, setShowRecoveryPlanDetails] = useState(false);
  const [showGiftScreen, setShowGiftScreen] = useState(false);
  const [deepDiveCode, setDeepDiveCode] = useState(null);
  const [giftScreenShown, setGiftScreenShown] = useState(false);
  const [lastGiftCheckInCount, setLastGiftCheckInCount] = useState(null);
  const giftScreenDismissedRef = useRef(false);
  const [showCapacityScreen, setShowCapacityScreen] = useState(false);
  const [capacityScore, setCapacityScore] = useState(null);
  const [capacityScreenShown, setCapacityScreenShown] = useState(false);
  const [lastCapacityCheckInCount, setLastCapacityCheckInCount] = useState(null);
  const capacityScreenDismissedRef = useRef(false);
  const [showPerformanceScreen, setShowPerformanceScreen] = useState(false);
  const [performanceScore, setPerformanceScore] = useState(null);
  const [performanceScreenShown, setPerformanceScreenShown] = useState(false);
  const [lastPerformanceCheckInCount, setLastPerformanceCheckInCount] = useState(null);
  const performanceScreenDismissedRef = useRef(false);
  const [showInfoOverlay, setShowInfoOverlay] = useState(false);
  const [recoveryPlanDisplayMode, setRecoveryPlanDisplayMode] = useState('points');
  
  // Ref to track if plan has been saved for current screen view
  const planSavedRef = useRef(false);
  
  // Determine if CTA button should be shown and get button text
  const ctaButtonConfig = useMemo(() => {
    const content = data?.content || {};
    
    // Check new ctaButtonType field first
    if (content.ctaButtonType === 'no_button') {
      return { show: false, text: 'Next' };
    }
    
    // Backward compatibility: check old showCta field
    if (content.showCta === false && !content.ctaButtonType) {
      return { show: false, text: 'Next' };
    }
    
    // Show button if ctaButtonType is 'next_screen_or_checkpoint' or if using old showCta (default true)
    const showButton = content.ctaButtonType === 'next_screen_or_checkpoint' || 
                       (content.ctaButtonType === undefined && content.showCta !== false);
    
    // Get button text: use ctaButtonText if provided, otherwise ctaText, otherwise default to 'Next'
    const buttonText = content.ctaButtonText || content.ctaText || 'Next';
    
    return { show: showButton, text: buttonText };
  }, [data?.content]);
  
  // Update optimal pace data when userProgress changes
  useEffect(() => {
    const newOptimalPaceData = userProgress.calculatedMetrics?.optimalPace;
    if (newOptimalPaceData) {
      setOptimalPaceData(newOptimalPaceData);
    }
  }, [userProgress]);

  // Load recovery plan display mode from userProgress
  useEffect(() => {
    if (userProgress?.recoveryPlanDisplayMode) {
      setRecoveryPlanDisplayMode(userProgress.recoveryPlanDisplayMode);
    }
  }, [userProgress?.recoveryPlanDisplayMode]);

  // Save recovery plan display mode to Firebase when it changes
  const handleDisplayModeChange = async (mode) => {
    if (mode === recoveryPlanDisplayMode) return; // No change needed
    
    setRecoveryPlanDisplayMode(mode);
    
    if (userProgress?.shortcode) {
      try {
        await updateUserProgress(userProgress.shortcode, {
          recoveryPlanDisplayMode: mode
        });
      } catch (error) {
        console.error('Error saving recovery plan display mode:', error);
      }
    }
  };

  // Show controls after 2 seconds - only start timer when Optimal Pace screen is actually displayed
  useEffect(() => {
    // Only start timer if:
    // 1. Not showing design interface
    // 2. Optimal pace data exists
    // 3. Gift screen is not being shown
    // 4. Capacity screen is not being shown
    // 5. Performance screen is not being shown (so Optimal Pace screen is visible)
    if (!showDesignInterface && optimalPaceData && !showGiftScreen && !showCapacityScreen && !showPerformanceScreen) {
      // Reset controls to false first, then start the 2-second timer
      setShowControls(false);
      const timer = setTimeout(() => {
        setShowControls(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      // Reset controls if we're not showing the Optimal Pace screen
      setShowControls(false);
    }
  }, [showDesignInterface, optimalPaceData, showGiftScreen, showCapacityScreen, showPerformanceScreen]);

  // Initialize selected hours and points from most recent journal entry if available
  useEffect(() => {
    // Only initialize once when both are null
    if (selectedHours !== null && selectedPoints !== null) {
      return;
    }
    
    if (!userProgress?.journalEntries || userProgress.journalEntries.length === 0) {
      // No journal entries, use optimal pace for hours if available
      if (selectedHours === null && optimalPaceData) {
        setSelectedHours(Math.round(optimalPaceData.optimalHours));
      }
      return;
    }
    
    // Find the most recent journal entry
    const journalEntries = userProgress.journalEntries;
    const getTimestampValue = (timestamp) => {
      if (!timestamp) return 0;
      if (timestamp instanceof Date) return timestamp.getTime();
      if (typeof timestamp === 'number') return timestamp;
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().getTime();
      }
      return new Date(timestamp).getTime() || 0;
    };
    
    const mostRecentEntry = journalEntries.reduce((latest, entry) => {
      const entryTime = getTimestampValue(entry.timestamp);
      const latestTime = getTimestampValue(latest.timestamp);
      return entryTime > latestTime ? entry : latest;
    }, journalEntries[0]);
    
    // Initialize hours from journal entry if available, otherwise from optimal pace
    if (selectedHours === null) {
      if (mostRecentEntry?.hoursPlan !== undefined && mostRecentEntry?.hoursPlan !== null) {
        setSelectedHours(mostRecentEntry.hoursPlan);
      } else if (optimalPaceData) {
        setSelectedHours(Math.round(optimalPaceData.optimalHours));
      }
    }
    
    // Initialize points from journal entry if available
    if (selectedPoints === null) {
      if (mostRecentEntry?.pointsPlan !== undefined && mostRecentEntry?.pointsPlan !== null) {
        setSelectedPoints(mostRecentEntry.pointsPlan);
      }
    }
  }, [userProgress?.journalEntries, optimalPaceData]);
  
  // Calculate points from hours if points weren't loaded from journal entry
  useEffect(() => {
    if (selectedPoints === null && selectedHours !== null && optimalPaceData) {
      const optimalPaceNumber = Math.round(optimalPaceData.optimalHours);
      const calculatedPoints = selectedHours <= optimalPaceNumber 
        ? 10 
        : 10 + 7 * (selectedHours - optimalPaceNumber);
      setSelectedPoints(calculatedPoints);
    }
  }, [selectedHours, optimalPaceData, selectedPoints]);
  
  // Fetch completed commitments count from founders-first project
  const fetchCompletedCommitments = useCallback(async () => {
    if (userProgress?.shortcode) {
      setIsRefreshingPoints(true);
      try {
        const count = await getCompletedCommitmentsCount(userProgress.shortcode);
        setCompletedCommitmentsCount(count);
      } catch (error) {
        console.error('Error fetching completed commitments count:', error);
        setCompletedCommitmentsCount(0);
      } finally {
        setIsRefreshingPoints(false);
      }
    }
  }, [userProgress?.shortcode]);

  useEffect(() => {
    fetchCompletedCommitments();
  }, [fetchCompletedCommitments]);
  
  // Generate random 10-digit code (uppercase letters and numbers)
  const generateDeepDiveCode = useCallback(() => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 10; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }, []);

  // Calculate and check if Gift (Deep Dive) screen should be shown
  useEffect(() => {
    if (!userProgress?.journalEntries || userProgress.journalEntries.length === 0) {
      setShowGiftScreen(false);
      setDeepDiveCode(null);
      giftScreenDismissedRef.current = false;
      return;
    }

    const totalCheckIns = userProgress.journalEntries.length;
    
    // Reset dismissed flag if check-in count has changed (new milestone)
    if (lastGiftCheckInCount !== null && lastGiftCheckInCount !== totalCheckIns) {
      giftScreenDismissedRef.current = false;
    }

    // If Gift screen has been dismissed by user for this check-in count, don't show it again
    if (giftScreenDismissedRef.current && lastGiftCheckInCount === totalCheckIns) {
      setShowGiftScreen(false);
      return;
    }

    // If Gift screen has already been shown, don't recalculate
    // It will remain visible until user clicks Next
    if (giftScreenShown) {
      return;
    }

    // Show Gift screen on every 25th check-in (25, 50, 75, etc.)
    // Only show if totalCheckIns is exactly divisible by 25 and greater than 0
    // AND we haven't already shown it for this check-in count
    const isTwentyFifthCheckIn = totalCheckIns > 0 && totalCheckIns % 25 === 0;
    const notAlreadyShown = lastGiftCheckInCount !== totalCheckIns;
    const notDismissed = !(giftScreenDismissedRef.current && lastGiftCheckInCount === totalCheckIns);
    const shouldShowGift = isTwentyFifthCheckIn && notAlreadyShown && notDismissed;

    if (shouldShowGift) {
      // Check if the most recent journal entry already has a deepdivecode
      const getTimestampValue = (timestamp) => {
        if (!timestamp) return 0;
        if (timestamp instanceof Date) return timestamp.getTime();
        if (typeof timestamp === 'number') return timestamp;
        if (timestamp && typeof timestamp.toDate === 'function') {
          return timestamp.toDate().getTime();
        }
        return new Date(timestamp).getTime() || 0;
      };
      
      const mostRecentEntry = userProgress.journalEntries.reduce((latest, entry) => {
        const entryTime = getTimestampValue(entry.timestamp);
        const latestTime = getTimestampValue(latest.timestamp);
        return entryTime > latestTime ? entry : latest;
      }, userProgress.journalEntries[0]);
      
      // Use existing code if available, otherwise generate a new one
      let code = mostRecentEntry?.deepdivecode;
      if (!code) {
        code = generateDeepDiveCode();
        
        // Save the new code to the most recent journal entry
        if (userProgress?.shortcode) {
          updateMostRecentJournalEntryPlan(userProgress.shortcode, {
            deepdivecode: code
          }).catch(error => {
            console.error('Error saving deep dive code to journal entry:', error);
          });
        }
      }
      
      setDeepDiveCode(code);
      setShowGiftScreen(true);
      setGiftScreenShown(true);
      setLastGiftCheckInCount(totalCheckIns);
    } else {
      // Conditions not met - ensure screen is hidden and code is cleared
      setShowGiftScreen(false);
      setDeepDiveCode(null);
    }
  }, [userProgress?.journalEntries, giftScreenShown, lastGiftCheckInCount, userProgress?.shortcode, generateDeepDiveCode]);

  // Calculate and check if Capacity screen should be shown
  useEffect(() => {
    if (!userProgress?.journalEntries || userProgress.journalEntries.length === 0) {
      setShowCapacityScreen(false);
      setCapacityScore(null);
      capacityScreenDismissedRef.current = false;
      return;
    }

    const totalCheckIns = userProgress.journalEntries.length;
    
    // Reset dismissed flag if check-in count has changed (new milestone)
    if (lastCapacityCheckInCount !== null && lastCapacityCheckInCount !== totalCheckIns) {
      capacityScreenDismissedRef.current = false;
    }

    // If Capacity screen has been dismissed by user for this check-in count, don't show it again
    if (capacityScreenDismissedRef.current && lastCapacityCheckInCount === totalCheckIns) {
      setShowCapacityScreen(false);
      return;
    }

    // If Capacity screen has already been shown, don't recalculate
    // It will remain visible until user clicks Next
    if (capacityScreenShown) {
      return;
    }

    // Show Capacity screen on every 7th check-in (7, 14, 21, etc.)
    // Only show if totalCheckIns is exactly divisible by 7 and greater than 0
    // AND we haven't already shown it for this check-in count
    // AND it hasn't been dismissed for this check-in count
    const isSeventhCheckIn = totalCheckIns > 0 && totalCheckIns % 7 === 0;
    const notAlreadyShown = lastCapacityCheckInCount !== totalCheckIns;
    const notDismissed = !(capacityScreenDismissedRef.current && lastCapacityCheckInCount === totalCheckIns);
    const shouldShowCapacity = isSeventhCheckIn && notAlreadyShown && notDismissed;

    if (shouldShowCapacity) {
      // Calculate capacity score (sum of last 7 Optimal Paces)
      const calculateCapacityScore = () => {
        try {
          const journalEntries = userProgress.journalEntries;
          const getTimestampValue = (timestamp) => {
            if (!timestamp) return 0;
            if (timestamp instanceof Date) return timestamp.getTime();
            if (typeof timestamp === 'number') return timestamp;
            if (timestamp && typeof timestamp.toDate === 'function') {
              return timestamp.toDate().getTime();
            }
            return new Date(timestamp).getTime() || 0;
          };

          // First, sort entries by timestamp to ensure chronological order
          const sortedEntries = [...journalEntries].sort((a, b) => {
            const timeA = getTimestampValue(a.timestamp);
            const timeB = getTimestampValue(b.timestamp);
            return timeA - timeB;
          });

          // Get last 7 entries that have hoursWorked
          const entriesWithHours = sortedEntries
            .filter(entry => entry.hoursWorked !== null && entry.hoursWorked !== undefined)
            .slice(-7); // Last 7 entries

          if (entriesWithHours.length === 0) {
            return null;
          }

          // Calculate optimal pace for each of the last 7 entries
          const optimalPaces = [];
          entriesWithHours.forEach((entry) => {
            // Find the index of this entry in the sorted journal entries array
            const entryIndex = sortedEntries.findIndex(e => 
              getTimestampValue(e.timestamp) === getTimestampValue(entry.timestamp)
            );
            
            if (entryIndex !== -1) {
              const paceResult = calculateEnhancedOptimalPace(sortedEntries, entryIndex);
              if (paceResult && paceResult.optimalHours !== undefined) {
                optimalPaces.push(paceResult.optimalHours);
              }
            }
          });

          if (optimalPaces.length === 0) {
            return null;
          }

          // Sum of last 7 Optimal Paces, rounded to nearest integer
          const sum = optimalPaces.reduce((total, pace) => total + pace, 0);
          return Math.round(sum);
        } catch (error) {
          console.error('Error calculating capacity score:', error);
          return null;
        }
      };

      const score = calculateCapacityScore();
      if (score !== null) {
        setCapacityScore(score);
        setShowCapacityScreen(true);
        setCapacityScreenShown(true);
        setLastCapacityCheckInCount(totalCheckIns);
      } else {
        // If score calculation failed, don't show the screen
        setShowCapacityScreen(false);
      }
    } else {
      // Conditions not met - ensure screen is hidden and score is cleared
      setShowCapacityScreen(false);
      setCapacityScore(null);
    }
  }, [userProgress?.journalEntries, capacityScreenShown, lastCapacityCheckInCount]);

  // Calculate and check if Performance screen should be shown
  useEffect(() => {
    if (!userProgress?.journalEntries || userProgress.journalEntries.length === 0) {
      setShowPerformanceScreen(false);
      setPerformanceScore(null);
      performanceScreenDismissedRef.current = false;
      return;
    }

    const totalCheckIns = userProgress.journalEntries.length;
    
    // Reset dismissed flag if check-in count has changed (new milestone)
    if (lastPerformanceCheckInCount !== null && lastPerformanceCheckInCount !== totalCheckIns) {
      performanceScreenDismissedRef.current = false;
    }

    // If Performance screen has been dismissed by user for this check-in count, don't show it again
    if (performanceScreenDismissedRef.current && lastPerformanceCheckInCount === totalCheckIns) {
      setShowPerformanceScreen(false);
      return;
    }

    // If Performance screen has already been shown, don't recalculate
    // It will remain visible until user clicks Next
    // BUT: if Capacity screen is showing, we still need to calculate Performance score
    // so it's ready when Capacity is dismissed
    if (performanceScreenShown && !showCapacityScreen) {
      return;
    }

    // Show Performance screen on every 3rd check-in (3, 6, 9, etc.)
    // Only show if totalCheckIns is exactly divisible by 3 and greater than 0
    // AND we haven't already shown it for this check-in count
    const shouldShowPerformance = totalCheckIns > 0 && 
                                   totalCheckIns % 3 === 0 && 
                                   lastPerformanceCheckInCount !== totalCheckIns;

    if (shouldShowPerformance) {
      // Calculate performance score
      const calculatePerformanceScore = () => {
        try {
          // Get most recent journal entry for today's data
          const journalEntries = userProgress.journalEntries;
          const getTimestampValue = (timestamp) => {
            if (!timestamp) return 0;
            if (timestamp instanceof Date) return timestamp.getTime();
            if (typeof timestamp === 'number') return timestamp;
            if (timestamp && typeof timestamp.toDate === 'function') {
              return timestamp.toDate().getTime();
            }
            return new Date(timestamp).getTime() || 0;
          };
          
          const mostRecentEntry = journalEntries.reduce((latest, entry) => {
            const entryTime = getTimestampValue(entry.timestamp);
            const latestTime = getTimestampValue(latest.timestamp);
            return entryTime > latestTime ? entry : latest;
          }, journalEntries[0]);

          if (!mostRecentEntry || !mostRecentEntry.emotionalStates) {
            return null;
          }

          // Calculate current mental reserves (average of today's factor scores)
          const emotionalFactors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
          const factorScores = emotionalFactors
            .map(factor => mostRecentEntry.emotionalStates[factor])
            .filter(val => val !== null && val !== undefined && val >= 0 && val <= 10);
          
          if (factorScores.length === 0) {
            return null;
          }

          const currentMentalReserves = factorScores.reduce((sum, val) => sum + val, 0) / factorScores.length;

          // Use fixed optimal target of 8
          const optimalTarget = 8;

          // Calculate average Optimal Pace for the last 5 check-ins
          // First, sort entries by timestamp to ensure chronological order
          const sortedEntries = [...journalEntries].sort((a, b) => {
            const timeA = getTimestampValue(a.timestamp);
            const timeB = getTimestampValue(b.timestamp);
            return timeA - timeB;
          });

          // Get last 5 entries that have hoursWorked
          const entriesWithHours = sortedEntries
            .filter(entry => entry.hoursWorked !== null && entry.hoursWorked !== undefined)
            .slice(-5); // Last 5 entries

          if (entriesWithHours.length === 0) {
            return null;
          }

          // Calculate optimal pace for each of the last 5 entries
          const optimalPaces = [];
          entriesWithHours.forEach((entry) => {
            // Find the index of this entry in the sorted journal entries array
            const entryIndex = sortedEntries.findIndex(e => 
              getTimestampValue(e.timestamp) === getTimestampValue(entry.timestamp)
            );
            
            if (entryIndex !== -1) {
              const paceResult = calculateEnhancedOptimalPace(sortedEntries, entryIndex);
              if (paceResult && paceResult.optimalHours !== undefined) {
                optimalPaces.push(paceResult.optimalHours);
              }
            }
          });

          if (optimalPaces.length === 0) {
            return null;
          }

          const averageOptimalPace = optimalPaces.reduce((sum, pace) => sum + pace, 0) / optimalPaces.length;

          // Calculate performance score
          // (10 - abs(current mental reserves - optimal target)) * average Optimal Pace
          const difference = Math.abs(currentMentalReserves - optimalTarget);
          const score = (10 - difference) * averageOptimalPace;

          return Math.round(score); // Round to nearest integer
        } catch (error) {
          console.error('Error calculating performance score:', error);
          return null;
        }
      };

      const score = calculatePerformanceScore();
      if (score !== null) {
        setPerformanceScore(score);
        setShowPerformanceScreen(true);
        setPerformanceScreenShown(true);
        setLastPerformanceCheckInCount(totalCheckIns);
      } else {
        // If score calculation failed, don't show the screen
        setShowPerformanceScreen(false);
      }
    } else {
      // Conditions not met - ensure screen is hidden
      setShowPerformanceScreen(false);
      // Don't clear performanceScore or lastPerformanceCheckInCount here
      // as they might be needed if user navigates back
    }
  }, [userProgress?.journalEntries, performanceScreenShown, lastPerformanceCheckInCount, showCapacityScreen]);

  // Helper function to calculate points - matches the screen logic exactly
  // This will be used by both the useEffect and the screen rendering
  const calculatePointsWithRefreshRate = useCallback((hours, optimalPace, refreshRateValue) => {
    // Base recovery points = (100 - Refresh Rate) / 100 * 40
    const basePoints = (100 - refreshRateValue) / 100 * 40;
    
    if (hours <= optimalPace) {
      return Math.round(basePoints);
    } else {
      // Extra points per hour above optimal = (100 - Refresh Rate) / 100 * 25
      const extraPointsPerHour = (100 - refreshRateValue) / 100 * 25;
      return Math.round(basePoints + extraPointsPerHour * (hours - optimalPace));
    }
  }, []);

  // Save plan when Optimal Pace screen is shown (if user doesn't modify it)
  // We need to ensure we use the same refreshRate calculation as the screen
  // The screen calculates refreshRate in the render, so we need to wait for the next render cycle
  useEffect(() => {
    // Only save if:
    // 1. Optimal Pace screen is visible (not showing Gift, Capacity, Performance, or Design Interface)
    // 2. We have optimal pace data
    // 3. We have user shortcode
    // 4. We haven't already saved for this screen view
    // 5. Recovery Plan is not active (user hasn't adjusted hours - they already saved when adjusting)
    if (
      !showDesignInterface &&
      !showGiftScreen &&
      !showCapacityScreen &&
      !showPerformanceScreen &&
      !isRecoveryPlan &&
      optimalPaceData &&
      userProgress?.shortcode &&
      !planSavedRef.current
    ) {
      // Use requestAnimationFrame to ensure we run after the screen has calculated refreshRate
      const frameId = requestAnimationFrame(() => {
        // Calculate display hours and points using the exact same logic as the screen
        const optimalHours = optimalPaceData.optimalHours;
        const optimalPaceNumber = Math.round(optimalHours);
        const calculatedDisplayHours = adjustedHours !== null ? adjustedHours : (selectedHours !== null ? selectedHours : optimalPaceNumber);
        
        // Get refresh rate using the exact same logic as the screen
        // This matches the screen's calculation order exactly
        let refreshRate = optimalPaceData?.refreshRate ?? userProgress?.calculatedMetrics?.refreshRate;
        if (refreshRate === null || refreshRate === undefined) {
          if (userProgress?.journalEntries && userProgress.journalEntries.length > 0) {
            const calculatedRefreshRate = calculateRefreshRate(userProgress.journalEntries);
            refreshRate = calculatedRefreshRate !== null ? calculatedRefreshRate : 0;
          } else {
            refreshRate = 0;
          }
        }
        
        // Calculate points, power, and plays using the exact same functions as the screen
        const calculatedDisplayPoints = calculatePointsWithRefreshRate(calculatedDisplayHours, optimalPaceNumber, refreshRate);
        
        // Calculate power
        const calculatePowerValue = (hours, optimalPace) => {
          if (hours <= optimalPace) {
            return 1;
          } else {
            const refreshComponent = (100 - refreshRate) / 100;
            const hoursAboveOptimal = hours - optimalPace;
            const hoursComponent = hoursAboveOptimal / 18;
            const powerMultiplier = 10 * (refreshComponent + hoursComponent);
            const roundedPower = Math.round(powerMultiplier * 2) / 2;
            return Math.max(1, Math.min(10, roundedPower));
          }
        };
        const calculatedDisplayPower = calculatePowerValue(calculatedDisplayHours, optimalPaceNumber);
        
        // Calculate plays
        const calculatePlaysValue = (hours, optimalPace) => {
          if (!userProgress?.journalEntries || userProgress.journalEntries.length === 0) {
            return 0;
          }
          const getTimestampValue = (timestamp) => {
            if (!timestamp) return 0;
            if (timestamp instanceof Date) return timestamp.getTime();
            if (typeof timestamp === 'number') return timestamp;
            if (timestamp && typeof timestamp.toDate === 'function') {
              return timestamp.toDate().getTime();
            }
            return new Date(timestamp).getTime() || 0;
          };
          const mostRecentEntry = userProgress.journalEntries.reduce((latest, entry) => {
            const entryTime = getTimestampValue(entry.timestamp);
            const latestTime = getTimestampValue(latest.timestamp);
            return entryTime > latestTime ? entry : latest;
          }, userProgress.journalEntries[0]);
          if (!mostRecentEntry || !mostRecentEntry.emotionalStates) {
            return 0;
          }
          const emotionalFactors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
          const differences = emotionalFactors.map(key => {
            const score = mostRecentEntry.emotionalStates[key];
            if (score === null || score === undefined) return null;
            return 10 - score;
          }).filter(diff => diff !== null && diff > 0);
          if (differences.length === 0) {
            return 0;
          }
          const difTotal = differences.reduce((sum, diff) => sum + diff, 0);
          if (difTotal === 0) {
            return 0;
          }
          const roundedHours = Math.round(hours);
          const roundedOptimalPace = Math.round(optimalPace);
          let playsAmount;
          if (roundedHours <= roundedOptimalPace) {
            playsAmount = ((100 - refreshRate) / 100) * 25;
          } else {
            const hoursAboveOptimal = roundedHours - roundedOptimalPace;
            playsAmount = ((100 - refreshRate) / 100) * (25 + (hoursAboveOptimal * 5));
          }
          return Math.round(playsAmount);
        };
        const calculatedDisplayPlays = calculatePlaysValue(calculatedDisplayHours, optimalPaceNumber);
        
        // Only save if we have valid values
        if (calculatedDisplayHours !== null && calculatedDisplayHours !== undefined) {
          const savePlan = async () => {
            try {
              // Build save object based on recovery plan display mode
              const planData = { hoursPlan: calculatedDisplayHours };
              if (recoveryPlanDisplayMode === 'plays') {
                planData.playsPlan = calculatedDisplayPlays;
              } else if (recoveryPlanDisplayMode === 'power') {
                planData.powerPlan = calculatedDisplayPower;
              } else {
                planData.pointsPlan = calculatedDisplayPoints;
              }
              
              await updateMostRecentJournalEntryPlan(userProgress.shortcode, planData);
              planSavedRef.current = true;
            } catch (error) {
              console.error('Error saving plan data when Optimal Pace screen is shown:', error);
            }
          };
          savePlan();
        }
      });
      
      return () => cancelAnimationFrame(frameId);
    }
    
    // Reset the saved flag when screen visibility changes
    if (showDesignInterface || showGiftScreen || showCapacityScreen || showPerformanceScreen) {
      planSavedRef.current = false;
    }
  }, [
    showDesignInterface,
    showGiftScreen,
    showCapacityScreen,
    showPerformanceScreen,
    isRecoveryPlan,
    optimalPaceData,
    adjustedHours,
    selectedHours,
    userProgress?.shortcode,
    userProgress?.journalEntries,
    userProgress?.calculatedMetrics?.refreshRate,
    calculatePointsWithRefreshRate,
    recoveryPlanDisplayMode
  ]);

  // Calculate remaining check-ins until next milestones
  const calculateRemainingCheckIns = useCallback(() => {
    const totalCheckIns = userProgress?.journalEntries?.length || 0;
    
    // Calculate remaining check-ins for Capacity (every 7th)
    const nextCapacityCheckIn = Math.ceil((totalCheckIns + 1) / 7) * 7;
    const capacityRemaining = Math.max(0, nextCapacityCheckIn - totalCheckIns);
    
    // Calculate remaining check-ins for Performance (every 3rd)
    const nextPerformanceCheckIn = Math.ceil((totalCheckIns + 1) / 3) * 3;
    const performanceRemaining = Math.max(0, nextPerformanceCheckIn - totalCheckIns);
    
    // Calculate remaining check-ins for Deep Dive (every 25th)
    const nextDeepDiveCheckIn = Math.ceil((totalCheckIns + 1) / 25) * 25;
    const deepDiveRemaining = Math.max(0, nextDeepDiveCheckIn - totalCheckIns);
    
    return {
      capacity: capacityRemaining,
      performance: performanceRemaining,
      deepDive: deepDiveRemaining
    };
  }, [userProgress?.journalEntries?.length]);

  // Process merge fields and format text content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Helper function to render the bar visualization
  const renderBarVisualization = (optimalHours) => {
    if (optimalHours === undefined || optimalHours === null) return null;

    // Calculate position of score indicator (0-18 scale)
    const scorePosition = Math.max(0, Math.min(18, optimalHours));
    // Adjust calculation to keep indicator within bar bounds (5% margin from each end)
    const indicatorLeft = `${5 + (scorePosition / 18) * 90}%`;

    // Conditionally hide labels based on Optimal Pace value
    const optimalPaceValue = Math.round(optimalHours);
    const hideZeroLabel = (optimalPaceValue === 0 || optimalPaceValue === 1);
    const hideEighteenLabel = (optimalPaceValue === 17 || optimalPaceValue === 18);

    return (
      <BarContainer>
        <BarWrapper>
          <BarLabels>
            {!hideZeroLabel && <LeftBarLabel>0</LeftBarLabel>}
            {!hideEighteenLabel && <RightBarLabel>18</RightBarLabel>}
          </BarLabels>
          
          <ScoreIndicator style={{ left: indicatorLeft }}>
            <ScoreLabel>{Math.round(optimalHours)}</ScoreLabel>
          </ScoreIndicator>
        </BarWrapper>
      </BarContainer>
    );
  };

  // Check if Gift should show (synchronously, before rendering other screens)
  const totalCheckIns = userProgress?.journalEntries?.length || 0;
  const shouldShowGiftSync = totalCheckIns > 0 && 
                             totalCheckIns % 25 === 0 && 
                             lastGiftCheckInCount !== totalCheckIns &&
                             !(giftScreenDismissedRef.current && lastGiftCheckInCount === totalCheckIns);

  // Render Gift (Deep Dive) screen if it should be shown (before all other screens)
  if ((showGiftScreen && deepDiveCode !== null) || (shouldShowGiftSync && deepDiveCode === null)) {
    // If Gift should show but code isn't generated yet, wait for useEffect
    if (deepDiveCode === null) {
      return null;
    }
    
    // Check if Capacity screen should also be shown
    const shouldShowCapacity = totalCheckIns > 0 && totalCheckIns % 7 === 0 && 
                                lastCapacityCheckInCount !== totalCheckIns &&
                                !(capacityScreenDismissedRef.current && lastCapacityCheckInCount === totalCheckIns);
    // Check if Performance screen should also be shown
    const shouldShowPerformance = totalCheckIns > 0 && totalCheckIns % 3 === 0 && 
                                   lastPerformanceCheckInCount !== totalCheckIns &&
                                   !(performanceScreenDismissedRef.current && lastPerformanceCheckInCount === totalCheckIns);

    return (
      <Container>
        <Content>
          <GiftIcon 
            src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Gift.svg?alt=media&token=a9236713-91ea-4ec2-8b4a-f5716964e27c"
            alt="Deep Dive"
          />
          <OptimalPaceHeading>Deep Dive</OptimalPaceHeading>
          <DeepDiveCode>{deepDiveCode}</DeepDiveCode>
          <DeepDiveInstructions>
            Screenshot and post in the Signal group to receive your deep dive review.
          </DeepDiveInstructions>
          <DeepDiveMessage>
            Don't miss this opportunity. You only get Deep Dives every 25 check-ins.
          </DeepDiveMessage>
          <NextButton onClick={() => {
            // Hide the Gift screen and mark it as dismissed
            // Keep lastGiftCheckInCount so it won't show again for this check-in milestone
            setShowGiftScreen(false);
            setGiftScreenShown(false);
            giftScreenDismissedRef.current = true;
            
            // If Capacity should show and has been calculated, trigger it to show next
            if (shouldShowCapacity && capacityScore !== null && !capacityScreenDismissedRef.current) {
              setShowCapacityScreen(true);
              setCapacityScreenShown(true);
            }
            // If Performance should show and has been calculated, trigger it to show next (if Capacity isn't showing)
            else if (shouldShowPerformance && performanceScore !== null && !performanceScreenDismissedRef.current) {
              setShowPerformanceScreen(true);
              setPerformanceScreenShown(true);
            }
          }}>
            Next
          </NextButton>
        </Content>
      </Container>
    );
  }

  // Check if Capacity should show (synchronously, before rendering Optimal Pace)
  const shouldShowCapacitySync = totalCheckIns > 0 && 
                                  totalCheckIns % 7 === 0 && 
                                  lastCapacityCheckInCount !== totalCheckIns &&
                                  !(capacityScreenDismissedRef.current && lastCapacityCheckInCount === totalCheckIns);

  // Render Capacity screen if it should be shown (before Performance and Optimal Pace)
  if ((showCapacityScreen && capacityScore !== null) || (shouldShowCapacitySync && capacityScore === null)) {
    // If Capacity should show but score isn't calculated yet, show loading state or wait
    if (capacityScore === null) {
      // Don't render anything yet, let useEffect calculate the score first
      return null;
    }
    
    // Check if Performance screen should also be shown
    const shouldShowPerformance = totalCheckIns > 0 && totalCheckIns % 5 === 0 && 
                                   lastPerformanceCheckInCount !== totalCheckIns &&
                                   !performanceScreenDismissedRef.current;

    return (
      <Container>
        <Content>
          <CapacityIcon 
            src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Capacity.svg?alt=media&token=368a6be4-8b49-493a-b078-794a3c2a6eab"
            alt="Capacity"
          />
          <OptimalPaceHeading>Capacity</OptimalPaceHeading>
          <CapacityScore>{capacityScore}</CapacityScore>
          <CapacityMessage>
            You'll see your Capacity score every 7 check-ins.
          </CapacityMessage>
          <NextButton onClick={() => {
            // Hide the Capacity screen and mark it as dismissed
            // Keep lastCapacityCheckInCount so it won't show again for this check-in milestone
            setShowCapacityScreen(false);
            setCapacityScreenShown(false);
            capacityScreenDismissedRef.current = true;
            
            // If Performance should show and has been calculated, trigger it to show next
            if (shouldShowPerformance && performanceScore !== null && !performanceScreenDismissedRef.current) {
              setShowPerformanceScreen(true);
              setPerformanceScreenShown(true);
            }
          }}>
            Next
          </NextButton>
        </Content>
      </Container>
    );
  }

  // Render Performance screen if it should be shown (before Optimal Pace)
  if (showPerformanceScreen && performanceScore !== null) {
    return (
      <Container>
        <Content>
          <PerformanceIcon 
            src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Performance.svg?alt=media&token=1df693fd-f975-4d96-b9a1-12b081102d5e"
            alt="Performance"
          />
          <OptimalPaceHeading>Performance</OptimalPaceHeading>
          <PerformanceScore>{performanceScore}</PerformanceScore>
          <PerformanceMessage>
            You'll see your Performance score every 3 check-ins.
          </PerformanceMessage>
          <NextButton onClick={() => {
            // Hide the Performance screen and mark it as dismissed
            // Keep lastPerformanceCheckInCount so it won't show again for this check-in milestone
            setShowPerformanceScreen(false);
            setPerformanceScreenShown(false);
            performanceScreenDismissedRef.current = true;
          }}>
            Next
          </NextButton>
        </Content>
      </Container>
    );
  }

  // Don't render Optimal Pace if Gift or Capacity should show (even if state hasn't updated yet)
  const shouldShowGiftSyncCheck = totalCheckIns > 0 && 
                                   totalCheckIns % 25 === 0 && 
                                   lastGiftCheckInCount !== totalCheckIns &&
                                   !(giftScreenDismissedRef.current && lastGiftCheckInCount === totalCheckIns);
  
  if (shouldShowGiftSyncCheck && !showGiftScreen) {
    // Gift should show but hasn't been generated yet - wait for useEffect
    return null;
  }
  
  if (shouldShowCapacitySync && !showCapacityScreen) {
    // Capacity should show but hasn't been calculated yet - wait for useEffect
    return null;
  }

  // Check if we have optimal pace data
  if (!optimalPaceData) {
    return (
      <Container>
        <Content>
          {htmlContent && (
            <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}
          
          <NoDataMessage>
            <NoDataTitle>No Data Available</NoDataTitle>
            <NoDataText>
              Complete a journal entry with hours worked to see your optimal pace calculation.
            </NoDataText>
          </NoDataMessage>
          
          {/* CTA button appears right after content */}
          {ctaButtonConfig.show && (
            <ContinueButton onClick={() => onNext?.(data.key)}>
              {ctaButtonConfig.text}
            </ContinueButton>
          )}
        </Content>
      </Container>
    );
  }

  const { optimalHours } = optimalPaceData;
  const optimalPaceNumber = Math.round(optimalHours);
  
  // Use adjustedHours if set, otherwise use selectedHours, otherwise use optimalPaceNumber
  const displayHours = adjustedHours !== null ? adjustedHours : (selectedHours !== null ? selectedHours : optimalPaceNumber);
  
  // Initialize selected hours if not set
  const currentSelectedHours = selectedHours !== null ? selectedHours : optimalPaceNumber;
  
  // Get refresh rate from calculated metrics, or calculate it on the fly if not available
  let refreshRate = optimalPaceData?.refreshRate ?? userProgress?.calculatedMetrics?.refreshRate;
  
  // If refresh rate is not available, calculate it from journal entries
  if (refreshRate === null || refreshRate === undefined) {
    if (userProgress?.journalEntries && userProgress.journalEntries.length > 0) {
      const calculatedRefreshRate = calculateRefreshRate(userProgress.journalEntries);
      refreshRate = calculatedRefreshRate !== null ? calculatedRefreshRate : 0;
    } else {
      refreshRate = 0;
    }
  }
  
  // Calculate points based on selected hours using Refresh Rate
  // Use the shared function with the refreshRate from this scope
  const calculatePoints = (hours, optimalPace) => {
    return calculatePointsWithRefreshRate(hours, optimalPace, refreshRate);
  };
  
  // Calculate power rating: 1x if at/below optimal pace, otherwise up to 10x based on refresh rate and hours above optimal
  const calculatePower = (hours, optimalPace) => {
    if (hours <= optimalPace) {
      return 1;
    } else {
      // Power = 10 × (((100 - Refresh Rate) / 100) + ((HoursPlan - OptimalPace) / 18))
      const refreshComponent = (100 - refreshRate) / 100;
      const hoursAboveOptimal = hours - optimalPace;
      const hoursComponent = hoursAboveOptimal / 18;
      const powerMultiplier = 10 * (refreshComponent + hoursComponent);
      // Round to nearest whole or half integer (0.5)
      const roundedPower = Math.round(powerMultiplier * 2) / 2;
      return Math.max(1, Math.min(10, roundedPower)); // Clamp 1-10
    }
  };
  
  // Calculate plays per factor based on emotional states
  const calculatePlays = (hours, optimalPace) => {
    if (!userProgress?.journalEntries || userProgress.journalEntries.length === 0) {
      return { totalPlays: 0, playsByFactor: [] };
    }
    
    // Get most recent journal entry
    const getTimestampValue = (timestamp) => {
      if (!timestamp) return 0;
      if (timestamp instanceof Date) return timestamp.getTime();
      if (typeof timestamp === 'number') return timestamp;
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().getTime();
      }
      return new Date(timestamp).getTime() || 0;
    };
    
    const mostRecentEntry = userProgress.journalEntries.reduce((latest, entry) => {
      const entryTime = getTimestampValue(entry.timestamp);
      const latestTime = getTimestampValue(latest.timestamp);
      return entryTime > latestTime ? entry : latest;
    }, userProgress.journalEntries[0]);
    
    if (!mostRecentEntry || !mostRecentEntry.emotionalStates) {
      return { totalPlays: 0, playsByFactor: [] };
    }
    
    // Emotional factors with their display names
    const emotionalFactors = [
      { key: 'mania', displayName: 'Control' },
      { key: 'depression', displayName: 'Satisfaction' },
      { key: 'energy', displayName: 'Energy' },
      { key: 'focus', displayName: 'Focus' },
      { key: 'calm', displayName: 'Calm' },
      { key: 'happiness', displayName: 'Positivity' },
      { key: 'hours', displayName: 'Personal Identity' }
    ];
    
    // Calculate difference from 10 for each factor
    const differences = emotionalFactors.map(factor => {
      const score = mostRecentEntry.emotionalStates[factor.key];
      if (score === null || score === undefined) return null;
      return {
        key: factor.key,
        displayName: factor.displayName,
        difference: 10 - score
      };
    }).filter(item => item !== null && item.difference > 0);
    
    if (differences.length === 0) {
      return { totalPlays: 0, playsByFactor: [] };
    }
    
    // Calculate DifTotal (sum of all differences)
    const difTotal = differences.reduce((sum, item) => sum + item.difference, 0);
    
    if (difTotal === 0) {
      return { totalPlays: 0, playsByFactor: [] };
    }
    
    // Calculate PlaysAmount based on new formula
    // Round both values before comparison (like Power and Points calculations)
    const roundedHours = Math.round(hours);
    const roundedOptimalPace = Math.round(optimalPace);
    
    let playsAmount;
    if (roundedHours <= roundedOptimalPace) {
      // If HoursPlan <= Optimal Pace: PlaysAmount = ((100-Refresh Rate)/100) × 25
      // Use same logic for both "at" and "below" optimal pace
      playsAmount = ((100 - refreshRate) / 100) * 25;
    } else {
      // If HoursPlan > Optimal Pace: PlaysAmount = ((100-Refresh Rate)/100) × (25 + ((HoursPlan - Optimal Pace) × 5))
      const hoursAboveOptimal = roundedHours - roundedOptimalPace;
      playsAmount = ((100 - refreshRate) / 100) * (25 + (hoursAboveOptimal * 5));
    }
    
    // Round PlaysAmount to get total plays to allocate
    const totalPlays = Math.round(playsAmount);
    
    if (totalPlays <= 0) {
      return { totalPlays: 0, playsByFactor: [] };
    }
    
    // Calculate percentage for each factor
    const factorsWithPercentages = differences.map(item => {
      const percentage = (item.difference / difTotal) * 100;
      const targetPlays = (percentage / 100) * totalPlays;
      return {
        key: item.key,
        displayName: item.displayName,
        percentage: percentage,
        targetPlays: targetPlays,
        basePlays: Math.floor(targetPlays), // Round down
        remainder: targetPlays - Math.floor(targetPlays) // Fractional part for round-robin
      };
    });
    
    // Calculate base allocation (sum of all rounded-down plays)
    const baseAllocation = factorsWithPercentages.reduce((sum, item) => sum + item.basePlays, 0);
    const remainingPlays = totalPlays - baseAllocation;
    
    // Allocate remaining plays round-robin to factors with highest remainders
    // Sort by remainder (descending) to prioritize factors with higher percentages
    const sortedFactors = [...factorsWithPercentages].sort((a, b) => b.remainder - a.remainder);
    
    // Allocate remaining plays one by one to factors with highest remainders
    const playsByFactor = sortedFactors.map((item, index) => {
      let plays = item.basePlays;
      // Give one extra play to the first 'remainingPlays' factors (those with highest remainders)
      if (index < remainingPlays) {
        plays += 1;
      }
      return {
        key: item.key,
        displayName: item.displayName,
        plays: plays
      };
    }).filter(item => item.plays > 0); // Only include factors with plays > 0
    
    return { totalPlays, playsByFactor };
  };
  
  // Check if display hours differ from optimal pace
  const isAtOptimalPace = displayHours === optimalPaceNumber;
  
  // Calculate recovery points for display
  const displayPoints = calculatePoints(displayHours, optimalPaceNumber);
  
  // Calculate power and plays
  const displayPower = calculatePower(displayHours, optimalPaceNumber);
  const playsData = calculatePlays(displayHours, optimalPaceNumber);
  
  // Handlers for adjusting hours
  const handleHoursIncrement = async () => {
    const newHours = displayHours + 1;
    if (newHours <= 18) {
      setAdjustedHours(newHours);
      if (showQuestion) {
        setShowQuestion(false);
        setIsRecoveryPlan(true);
      }
      
      // Calculate points, power, and plays for the new hours and save to journal entry
      const newPoints = calculatePoints(newHours, optimalPaceNumber);
      const newPower = calculatePower(newHours, optimalPaceNumber);
      const newPlaysData = calculatePlays(newHours, optimalPaceNumber);
      
      if (userProgress?.shortcode) {
        try {
          // Build save object based on recovery plan display mode
          const planData = { hoursPlan: newHours };
          if (recoveryPlanDisplayMode === 'plays') {
            planData.playsPlan = newPlaysData.totalPlays;
          } else if (recoveryPlanDisplayMode === 'power') {
            planData.powerPlan = newPower;
          } else {
            planData.pointsPlan = newPoints;
          }
          
          await updateMostRecentJournalEntryPlan(userProgress.shortcode, planData);
        } catch (error) {
          console.error('Error saving plan data on hours increment:', error);
        }
      }
    }
  };
  
  const handleHoursDecrement = async () => {
    const newHours = displayHours - 1;
    if (newHours >= 0) {
      setAdjustedHours(newHours);
      if (showQuestion) {
        setShowQuestion(false);
        setIsRecoveryPlan(true);
      }
      
      // Calculate points, power, and plays for the new hours and save to journal entry
      const newPoints = calculatePoints(newHours, optimalPaceNumber);
      const newPower = calculatePower(newHours, optimalPaceNumber);
      const newPlaysData = calculatePlays(newHours, optimalPaceNumber);
      
      if (userProgress?.shortcode) {
        try {
          // Build save object based on recovery plan display mode
          const planData = { hoursPlan: newHours };
          if (recoveryPlanDisplayMode === 'plays') {
            planData.playsPlan = newPlaysData.totalPlays;
          } else if (recoveryPlanDisplayMode === 'power') {
            planData.powerPlan = newPower;
          } else {
            planData.pointsPlan = newPoints;
          }
          
          await updateMostRecentJournalEntryPlan(userProgress.shortcode, planData);
        } catch (error) {
          console.error('Error saving plan data on hours decrement:', error);
        }
      }
    }
  };
  
  const handleNumberClick = () => {
    if (showQuestion) {
      setShowQuestion(false);
      setIsRecoveryPlan(true);
    }
  };
  
  const handleRecoveryPlanIconClick = () => {
    // Toggle Recovery Plan details - clicking again returns to Optimal Pace screen
    if (showRecoveryPlanDetails) {
      setShowRecoveryPlanDetails(false);
    } else {
      setShowRecoveryPlanDetails(true);
      // Fetch completed commitments when showing details
      if (userProgress?.shortcode && completedCommitmentsCount === null) {
        fetchCompletedCommitments();
      }
    }
  };
  
  // Calculate points for the design interface (using currentSelectedHours)
  const requiredPoints = calculatePoints(currentSelectedHours, optimalPaceNumber);
  
  // Initialize selected points if not set
  const currentSelectedPoints = selectedPoints !== null ? selectedPoints : requiredPoints;
  
  // Handlers for points adjustment
  const handlePointsDecrement = () => {
    if (currentSelectedPoints > 0) {
      setSelectedPoints(currentSelectedPoints - 1);
    }
  };
  
  const handlePointsIncrement = () => {
    setSelectedPoints(currentSelectedPoints + 1);
  };
  
  // Calculate balance visualization properties
  const isBalanced = currentSelectedPoints === requiredPoints;
  const pointsDifference = currentSelectedPoints - requiredPoints;
  
  // Base size for circles
  const baseSize = 70;
  const baseSizeMobile = 60;
  const sizeVariation = 25; // Max variation in pixels
  
  // Calculate size based on imbalance
  // If points < required: hours (left) is heavier, left circle bigger
  // If points > required: points (right) is heavier, right circle bigger
  // If balanced: both same size
  let leftSize = baseSize;
  let rightSize = baseSize;
  let leftSizeMobile = baseSizeMobile;
  let rightSizeMobile = baseSizeMobile;
  
  let tilt = 0;
  const maxTilt = 15;
  
  if (pointsDifference < 0) {
    // Not enough points - left (hours) is heavier
    const imbalanceRatio = Math.abs(pointsDifference) / Math.max(requiredPoints, 10);
    const sizeDiff = Math.min(sizeVariation, imbalanceRatio * sizeVariation);
    leftSize = baseSize + sizeDiff;
    rightSize = baseSize - sizeDiff;
    leftSizeMobile = baseSizeMobile + (sizeDiff * baseSizeMobile / baseSize);
    rightSizeMobile = baseSizeMobile - (sizeDiff * baseSizeMobile / baseSize);
    // Tilt toward left (positive tilt)
    tilt = Math.min(maxTilt, imbalanceRatio * maxTilt);
  } else if (pointsDifference > 0) {
    // Extra points - right (points) is heavier
    const imbalanceRatio = pointsDifference / Math.max(requiredPoints, 10);
    const sizeDiff = Math.min(sizeVariation, imbalanceRatio * sizeVariation);
    leftSize = baseSize - sizeDiff;
    rightSize = baseSize + sizeDiff;
    leftSizeMobile = baseSizeMobile - (sizeDiff * baseSizeMobile / baseSize);
    rightSizeMobile = baseSizeMobile + (sizeDiff * baseSizeMobile / baseSize);
    // Tilt toward right (negative tilt)
    tilt = -Math.min(maxTilt, imbalanceRatio * maxTilt);
  }
  
  // Calculate vertical offsets for circles to move with tilted bar
  // Horizontal distances from center: left = -140px, right = +140px (desktop)
  // Horizontal distances from center: left = -120px, right = +120px (mobile)
  const tiltRadians = (tilt * Math.PI) / 180;
  const leftHorizontalDist = -140; // pixels from center
  const rightHorizontalDist = 140; // pixels from center
  const leftHorizontalDistMobile = -120;
  const rightHorizontalDistMobile = 120;
  
  // Base offset from bar tilt - inverted so heavier side goes down
  // When bar tilts left (positive), left side should go down (positive offset)
  const leftBaseOffset = -leftHorizontalDist * Math.tan(tiltRadians); // Invert sign
  const rightBaseOffset = -rightHorizontalDist * Math.tan(tiltRadians); // Invert sign
  const leftBaseOffsetMobile = -leftHorizontalDistMobile * Math.tan(tiltRadians);
  const rightBaseOffsetMobile = -rightHorizontalDistMobile * Math.tan(tiltRadians);
  
  // Additional "falling" offset for larger/heavier circle (positive = down)
  // Max fall distance based on size difference
  const maxFallDistance = 15; // pixels
  const sizeDiff = Math.abs(leftSize - rightSize);
  const sizeDiffMobile = Math.abs(leftSizeMobile - rightSizeMobile);
  const fallRatio = Math.min(1, sizeDiff / sizeVariation);
  const fallRatioMobile = Math.min(1, sizeDiffMobile / sizeVariation);
  
  let leftFallOffset = 0;
  let rightFallOffset = 0;
  let leftFallOffsetMobile = 0;
  let rightFallOffsetMobile = 0;
  
  if (leftSize > rightSize) {
    // Left circle is heavier - it falls down
    leftFallOffset = fallRatio * maxFallDistance;
    leftFallOffsetMobile = fallRatioMobile * maxFallDistance;
  } else if (rightSize > leftSize) {
    // Right circle is heavier - it falls down
    rightFallOffset = fallRatio * maxFallDistance;
    rightFallOffsetMobile = fallRatioMobile * maxFallDistance;
  }
  
  // Total vertical offset: bar tilt offset + falling offset
  const leftVerticalOffset = leftBaseOffset + leftFallOffset;
  const rightVerticalOffset = rightBaseOffset + rightFallOffset;
  const leftVerticalOffsetMobile = leftBaseOffsetMobile + leftFallOffsetMobile;
  const rightVerticalOffsetMobile = rightBaseOffsetMobile + rightFallOffsetMobile;
  
  // Determine bar color based on tilt
  let barColor = '#7FB069'; // Lighter green when balanced (flat)
  if (tilt > 0) {
    barColor = '#FF8C42'; // Orange when positive slope (tilted left)
  } else if (tilt < 0) {
    barColor = theme.colors.secondary; // Blue when negative slope (tilted right)
  }
  
  // Render energy balance visualization
  const renderEnergyBalance = () => {
    return (
      <BalanceContainer>
        <BalanceScale>
          <ScaleBase $baseColor={barColor} />
          <ScaleBar 
            $isBalanced={isBalanced} 
            $tilt={-tilt} 
            $barWidth="320px" 
            $barWidthMobile="280px"
            $barColor={barColor}
          />
          
          <LeftSide $verticalOffset={leftVerticalOffset} $verticalOffsetMobile={leftVerticalOffsetMobile}>
            <BalanceWeight 
              $color={theme.colors.accent}
              $size={`${leftSize}px`}
              $sizeMobile={`${leftSizeMobile}px`}
              $fontSize="1.75rem"
              $fontSizeMobile="1.5rem"
            >
              {currentSelectedHours}
            </BalanceWeight>
            <BalanceLabel>Hours</BalanceLabel>
          </LeftSide>
          
          <RightSide $verticalOffset={rightVerticalOffset} $verticalOffsetMobile={rightVerticalOffsetMobile}>
            <BalanceWeight 
              $color={theme.colors.secondary}
              $size={`${rightSize}px`}
              $sizeMobile={`${rightSizeMobile}px`}
              $fontSize="1.75rem"
              $fontSizeMobile="1.5rem"
            >
              {currentSelectedPoints}
            </BalanceWeight>
            <BalanceLabel>Points</BalanceLabel>
          </RightSide>
        </BalanceScale>
      </BalanceContainer>
    );
  };
  
  // Render 24-hour day visualization
  const render24HourDay = () => {
    const totalHours = 24;
    const workHours = currentSelectedHours;
    const recoveryMinutes = currentSelectedPoints * 5; // Each point = 5 minutes
    const recoveryHours = recoveryMinutes / 60;
    const remainingHours = Math.max(0, totalHours - workHours - recoveryHours);
    
    const workPercentage = (workHours / totalHours) * 100;
    const recoveryPercentage = (recoveryHours / totalHours) * 100;
    const remainingPercentage = (remainingHours / totalHours) * 100;
    
    return (
      <DayBarContainer>
        <DayBarLabel>Your Time Allocation Today</DayBarLabel>
        <DayBarWrapper>
          <DayBarSegment 
            $color={theme.colors.accent}
            $percentage={workPercentage}
            style={{ width: `${workPercentage}%` }}
          />
          <DayBarSegment 
            $color={theme.colors.secondary}
            $percentage={recoveryPercentage}
            style={{ width: `${recoveryPercentage}%` }}
          />
          <DayBarSegment 
            $color={theme.colors.doryDarkerBlue}
            $percentage={remainingPercentage}
            style={{ width: `${remainingPercentage}%` }}
          >
            {remainingHours > 0 && remainingPercentage > 5 && (() => {
              const roundedHours = Math.round(remainingHours);
              return (
                <DayBarSegmentText>
                  {roundedHours} hour{roundedHours !== 1 ? 's' : ''}
                </DayBarSegmentText>
              );
            })()}
          </DayBarSegment>
        </DayBarWrapper>
        <DayBarKey>
          <DayBarKeyItem>
            <DayBarKeyColor $color={theme.colors.accent} />
            <span>Work</span>
          </DayBarKeyItem>
          <DayBarKeyItem>
            <DayBarKeyColor $color={theme.colors.secondary} />
            <span>Active Recovery</span>
          </DayBarKeyItem>
          <DayBarKeyItem>
            <DayBarKeyColor $color={theme.colors.doryDarkerBlue} />
            <span>Personal Time and Sleep</span>
          </DayBarKeyItem>
        </DayBarKey>
      </DayBarContainer>
    );
  };
  
  // Handlers for hour adjustment
  const handleDecrement = () => {
    if (currentSelectedHours > 0) {
      setSelectedHours(currentSelectedHours - 1);
    }
  };
  
  const handleIncrement = () => {
    if (currentSelectedHours < 18) {
      setSelectedHours(currentSelectedHours + 1);
    }
  };

  // Calculate remaining check-ins for overlay
  const remainingCheckIns = calculateRemainingCheckIns();

  // Render info overlay if shown
  if (showInfoOverlay) {
    // Create array of milestone items and sort by remaining check-ins (soonest first)
    const milestones = [
      {
        name: 'Capacity',
        icon: 'https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Capacity.svg?alt=media&token=368a6be4-8b49-493a-b078-794a3c2a6eab',
        remaining: remainingCheckIns.capacity
      },
      {
        name: 'Performance',
        icon: 'https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Performance.svg?alt=media&token=1df693fd-f975-4d96-b9a1-12b081102d5e',
        remaining: remainingCheckIns.performance
      },
      {
        name: 'Deep Dive',
        icon: 'https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Gift.svg?alt=media&token=a9236713-91ea-4ec2-8b4a-f5716964e27c',
        remaining: remainingCheckIns.deepDive
      }
    ].sort((a, b) => a.remaining - b.remaining);

    return (
      <>
        <OverlayContainer onClick={(e) => {
          // Close overlay if clicking on backdrop
          if (e.target === e.currentTarget) {
            setShowInfoOverlay(false);
          }
        }}>
          <OverlayContent onClick={(e) => e.stopPropagation()}>
            <OverlayCloseButton onClick={() => setShowInfoOverlay(false)}>
              ×
            </OverlayCloseButton>
            
            {milestones.map((milestone, index) => (
              <OverlayItem key={milestone.name}>
                <OverlayIcon 
                  src={milestone.icon}
                  alt={milestone.name}
                />
                <OverlayHeading>{milestone.name}</OverlayHeading>
                <OverlayMessage>
                  after {milestone.remaining} more check-in{milestone.remaining !== 1 ? 's' : ''}
                </OverlayMessage>
              </OverlayItem>
            ))}
          </OverlayContent>
        </OverlayContainer>
      </>
    );
  }

  return (
    <Container>
      <Content>
        {/* Compact Optimal Pace Box - shown at top after Design My Day button is clicked */}
        {showDesignInterface && (
          <>
            <CompactOptimalPaceBox>
              Optimal Pace is <strong>{optimalPaceNumber}</strong> hour{optimalPaceNumber !== 1 ? 's' : ''}
            </CompactOptimalPaceBox>
            
            {/* Start Tracking Button - shown right below Optimal Pace box */}
            {!isPlanLocked && (
              <LockInButton onClick={() => setIsPlanLocked(true)}>
                Start Tracking
              </LockInButton>
            )}
          </>
        )}
        
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* Plan Summary Box - shown when plan is locked */}
        {isPlanLocked && (
          <PlanSummaryWrapper>
            <PlanSummaryBox>
              You plan to work <strong>{currentSelectedHours}</strong> hour{currentSelectedHours !== 1 ? 's' : ''} and actively recover with <strong>{currentSelectedPoints}</strong> point{currentSelectedPoints !== 1 ? 's' : ''} (<ChangePlanLink onClick={() => setIsPlanLocked(false)}>adjust plan</ChangePlanLink>)
            </PlanSummaryBox>
          </PlanSummaryWrapper>
        )}
        
        {/* Total points earned from completed commitments for today - only shown when plan is locked */}
        {isPlanLocked && (
          <TotalPointsEarned>
            <HeadingRow>
              <div>Recovery Points</div>
              {(isRefreshingPoints || completedCommitmentsCount === null) ? (
                <LoadingSpinner />
              ) : (
                <RefreshLink onClick={fetchCompletedCommitments}>(refresh)</RefreshLink>
              )}
            </HeadingRow>
            {completedCommitmentsCount !== null && currentSelectedPoints > 0 && (
              <ProgressBarContainer>
                <ProgressBarFill 
                  $percentage={Math.min(100, (completedCommitmentsCount / currentSelectedPoints) * 100)}
                />
              </ProgressBarContainer>
            )}
            {completedCommitmentsCount !== null && currentSelectedPoints > 0 && (
              <div style={{ 
                fontFamily: theme.typography.bodyFont, 
                fontSize: theme.typography.sizes.small, 
                color: theme.colors.textLight,
                fontWeight: 'normal'
              }}>
                {completedCommitmentsCount >= currentSelectedPoints ? (
                  "You've completed your Recovery Plan for today."
                ) : (
                  `${completedCommitmentsCount.toLocaleString()} of ${currentSelectedPoints.toLocaleString()} points`
                )}
              </div>
            )}
          </TotalPointsEarned>
        )}
        
        {/* Info icon - shown on Optimal Pace and Recovery Plan screens, Deep Dive icon */}
        {!showDesignInterface && (
          <InfoIcon 
            src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Gift.svg?alt=media&token=a9236713-91ea-4ec2-8b4a-f5716964e27c"
            alt="View upcoming milestones"
            onClick={() => setShowInfoOverlay(true)}
            title="View upcoming milestones"
          />
        )}
        
        {/* Icon - changes to Recovery Plan when user adjusts */}
        {!showDesignInterface && (
          isRecoveryPlan ? (
            <RecoveryPlanIcon 
              src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Recovery%20Plan.svg?alt=media&token=6321eb35-83ae-479a-9530-62d96c4526b5"
              alt="Recovery Plan"
              onClick={handleRecoveryPlanIconClick}
            />
          ) : (
            <OptimalPaceIcon 
              src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/Optimal%20Pace.svg?alt=media&token=a0cd773e-b5f5-47f0-bd5d-7b95011315d4"
              alt="Optimal Pace"
            />
          )
        )}
        
        {/* Heading - changes to Recovery Plan when user adjusts */}
        {!showDesignInterface && (
          <OptimalPaceHeading>
            {isRecoveryPlan ? 'Recovery Plan' : 'Optimal Pace'}
          </OptimalPaceHeading>
        )}
        
        {/* Large Optimal Pace Number with +/- controls - hidden when Recovery Plan details are shown */}
        {!showDesignInterface && !showRecoveryPlanDetails && (
          <>
            <NumberContainer>
              <DecrementButton 
                onClick={handleHoursDecrement}
                disabled={displayHours === 0}
                $visible={showControls}
              >
                −
              </DecrementButton>
              <NumberWrapper>
                <OptimalPaceNumber 
                  $color={isAtOptimalPace ? '#435F60' : theme.colors.primary}
                  $isEditable={showControls}
                  onClick={showControls ? handleNumberClick : undefined}
                >
                  {displayHours}
                </OptimalPaceNumber>
                {isRecoveryPlan && isAtOptimalPace && (
                  <OptimalPaceLabel>
                    Optimal Pace
                  </OptimalPaceLabel>
                )}
              </NumberWrapper>
              <IncrementButton 
                onClick={handleHoursIncrement}
                disabled={displayHours === 18}
                $visible={showControls}
              >
                +
              </IncrementButton>
            </NumberContainer>
            
            {/* Question or Confirmation text */}
            {showControls && (
              <>
                {showQuestion ? (
                  <QuestionText>How many hours will you work today?</QuestionText>
                ) : (
                  <div style={{ marginBottom: isRecoveryPlan ? theme.spacing.lg : 0 }}>
                    <QuestionText>
                      {recoveryPlanDisplayMode === 'power' ? (
                        <>You'll work <strong>{displayHours}</strong> hour{displayHours !== 1 ? 's' : ''} today and recover with <strong>{displayPower}x</strong> power.</>
                      ) : recoveryPlanDisplayMode === 'plays' ? (
                        <>You'll work <strong>{displayHours}</strong> hour{displayHours !== 1 ? 's' : ''} today and recover with <strong>{playsData.totalPlays}</strong> play{playsData.totalPlays !== 1 ? 's' : ''}.</>
                      ) : (
                        <>You'll work <strong>{displayHours}</strong> hour{displayHours !== 1 ? 's' : ''} today and recover with <strong>{displayPoints}</strong> point{displayPoints !== 1 ? 's' : ''}.</>
                      )}
                    </QuestionText>
                  </div>
                )}
              </>
            )}
          </>
        )}
        
        {/* CTA button on Optimal Pace screen when Recovery Plan icon is shown */}
        {!showDesignInterface && !showRecoveryPlanDetails && isRecoveryPlan && ctaButtonConfig.show && (
          <ContinueButton onClick={async () => {
            // Don't save here - data was already saved when user adjusted hours
            onNext?.(data.key);
          }}>
            {ctaButtonConfig.text}
          </ContinueButton>
        )}
        
        {/* Recovery Plan Details - shown when icon is clicked */}
        {!showDesignInterface && showRecoveryPlanDetails && isRecoveryPlan && (
          <>
            {/* Confirmation text */}
            <QuestionText>
              {recoveryPlanDisplayMode === 'power' ? (
                <>You'll work <strong>{displayHours}</strong> hour{displayHours !== 1 ? 's' : ''} today and recover with <strong>{displayPower}x</strong> power.</>
              ) : recoveryPlanDisplayMode === 'plays' ? (
                <>You'll work <strong>{displayHours}</strong> hour{displayHours !== 1 ? 's' : ''} today and recover with <strong>{playsData.totalPlays}</strong> play{playsData.totalPlays !== 1 ? 's' : ''}.</>
              ) : (
                <>You'll work <strong>{displayHours}</strong> hour{displayHours !== 1 ? 's' : ''} today and recover with <strong>{displayPoints}</strong> point{displayPoints !== 1 ? 's' : ''}.</>
              )}
            </QuestionText>
            
            {/* Three-way toggle bar */}
            <ToggleBarContainer>
              <ToggleOption
                $isActive={recoveryPlanDisplayMode === 'power'}
                onClick={() => handleDisplayModeChange('power')}
              >
                Power
              </ToggleOption>
              <ToggleOption
                $isActive={recoveryPlanDisplayMode === 'plays'}
                onClick={() => handleDisplayModeChange('plays')}
              >
                Plays
              </ToggleOption>
              <ToggleOption
                $isActive={recoveryPlanDisplayMode === 'points'}
                onClick={() => handleDisplayModeChange('points')}
              >
                Points
              </ToggleOption>
            </ToggleBarContainer>
            
            {/* Conditional content based on display mode */}
            {recoveryPlanDisplayMode === 'points' && (
              <TotalPointsEarned>
                <HeadingRow>
                  <div>Recovery Points</div>
                  {(isRefreshingPoints || completedCommitmentsCount === null) ? (
                    <LoadingSpinner />
                  ) : (
                    <RefreshLink onClick={fetchCompletedCommitments}>(refresh)</RefreshLink>
                  )}
                </HeadingRow>
                {completedCommitmentsCount !== null && displayPoints > 0 && (
                  <ProgressBarContainer>
                    <ProgressBarFill 
                      $percentage={Math.min(100, (completedCommitmentsCount / displayPoints) * 100)}
                    />
                  </ProgressBarContainer>
                )}
                {completedCommitmentsCount !== null && displayPoints > 0 && (
                  <div style={{ 
                    fontFamily: theme.typography.bodyFont, 
                    fontSize: theme.typography.sizes.small, 
                    color: theme.colors.textLight,
                    fontWeight: 'normal'
                  }}>
                    {completedCommitmentsCount >= displayPoints ? (
                      "You've completed your Recovery Plan for today."
                    ) : (
                      `${completedCommitmentsCount.toLocaleString()} of ${displayPoints.toLocaleString()} points`
                    )}
                  </div>
                )}
              </TotalPointsEarned>
            )}
            
            {recoveryPlanDisplayMode === 'power' && (
              <TotalPointsEarned>
                <HeadingRow>
                  <div>Recovery Power</div>
                </HeadingRow>
                <PowerDisplay>{displayPower}x</PowerDisplay>
              </TotalPointsEarned>
            )}
            
            {recoveryPlanDisplayMode === 'plays' && (
              <TotalPointsEarned>
                <HeadingRow>
                  <div>Recovery Plays</div>
                </HeadingRow>
                {playsData.playsByFactor.length > 0 ? (
                  <PlaysListContainer>
                    {playsData.playsByFactor.map((item) => (
                      <PlayItem key={item.key}>
                        {item.plays} for {item.displayName}
                      </PlayItem>
                    ))}
                  </PlaysListContainer>
                ) : (
                  <div style={{ 
                    fontFamily: theme.typography.bodyFont, 
                    fontSize: theme.typography.sizes.body, 
                    color: theme.colors.textLight,
                    textAlign: 'center',
                    marginTop: theme.spacing.md
                  }}>
                    No plays needed - all factors are at optimal levels.
                  </div>
                )}
              </TotalPointsEarned>
            )}
            
            {/* CTA button on Recovery Plan screen */}
            {ctaButtonConfig.show && (
              <ContinueButton onClick={async () => {
                // Don't save here - data was already saved when user adjusted hours
                onNext?.(data.key);
              }}>
                {ctaButtonConfig.text}
              </ContinueButton>
            )}
          </>
        )}
        
        {/* Design Interface - shown only after button click */}
        {showDesignInterface && !isPlanLocked && (
          <>
            {/* Selectors row - hours on left, points on right */}
            <SelectorsRow>
              {/* Hours selector */}
              <SelectorContainer>
                <SelectorLabel>Work Hours</SelectorLabel>
                <SelectorControls>
                  <HourButton onClick={handleDecrement} disabled={currentSelectedHours === 0}>
                    −
                  </HourButton>
                  <HourNumber>{currentSelectedHours}</HourNumber>
                  <HourButton onClick={handleIncrement} disabled={currentSelectedHours === 18}>
                    +
                  </HourButton>
                </SelectorControls>
              </SelectorContainer>
              
              {/* Points selector */}
              <SelectorContainer>
                <SelectorLabel>Recovery Points</SelectorLabel>
                <SelectorControls>
                  <HourButton 
                    onClick={handlePointsDecrement} 
                    disabled={currentSelectedPoints === 0}
                    $backgroundColor={theme.colors.secondary}
                    $hoverColor={theme.colors.doryLighterBlue}
                  >
                    −
                  </HourButton>
                  <HourNumber $color={theme.colors.secondary}>{currentSelectedPoints}</HourNumber>
                  <HourButton 
                    onClick={handlePointsIncrement}
                    $backgroundColor={theme.colors.secondary}
                    $hoverColor={theme.colors.doryLighterBlue}
                  >
                    +
                  </HourButton>
                </SelectorControls>
              </SelectorContainer>
            </SelectorsRow>
            
            {/* Instruction text - all conditional states */}
            <InstructionText 
              dangerouslySetInnerHTML={{
                __html: (() => {
                  if (currentSelectedPoints < requiredPoints) {
                    return `You won't fully recover from your <strong>${currentSelectedHours}</strong> hour${currentSelectedHours !== 1 ? 's' : ''} of work today with only <strong>${currentSelectedPoints}</strong> point${currentSelectedPoints !== 1 ? 's' : ''}. Add more points or reduce work hours.`;
                  } else if (currentSelectedPoints > requiredPoints) {
                    return "You can remove some points and still have a balanced day. Or keep your points goal and finish the day stronger than you started.";
                  } else {
                    return `You have a balanced plan for today. You'll work <strong>${currentSelectedHours}</strong> hour${currentSelectedHours !== 1 ? 's' : ''} today and complete Plays in the Coach Dory app worth <strong>${currentSelectedPoints}</strong> point${currentSelectedPoints !== 1 ? 's' : ''} to actively recover.`;
                  }
                })()
              }}
            />
            
            {/* Energy Balance Visualization */}
            {renderEnergyBalance()}
            
            {/* 24-Hour Day Visualization */}
            {render24HourDay()}
            
            {/* CTA button appears right after content */}
            {ctaButtonConfig.show && (
              <ContinueButton onClick={async () => {
                // Only save if user hasn't adjusted hours (Recovery Plan not active)
                // If isRecoveryPlan is true, data was already saved when user adjusted hours
                if (!isRecoveryPlan && selectedPoints !== null && selectedHours !== null && userProgress?.shortcode) {
                  try {
                    // Calculate power and plays for the selected hours
                    const savePower = calculatePower(selectedHours, optimalPaceNumber);
                    const savePlaysData = calculatePlays(selectedHours, optimalPaceNumber);
                    
                    // Build save object based on recovery plan display mode
                    const planData = { hoursPlan: selectedHours };
                    if (recoveryPlanDisplayMode === 'plays') {
                      planData.playsPlan = savePlaysData.totalPlays;
                    } else if (recoveryPlanDisplayMode === 'power') {
                      planData.powerPlan = savePower;
                    } else {
                      planData.pointsPlan = selectedPoints;
                    }
                    
                    await updateMostRecentJournalEntryPlan(userProgress.shortcode, planData);
                  } catch (error) {
                    console.error('Error saving plan data on continue:', error);
                  }
                }
                onNext?.(data.key);
              }}>
                {ctaButtonConfig.text}
              </ContinueButton>
            )}
          </>
        )}
        
        {/* Continue button when plan is locked */}
        {isPlanLocked && ctaButtonConfig.show && (
          <ContinueButton onClick={async () => {
            // Only save if user hasn't adjusted hours (Recovery Plan not active)
            // If isRecoveryPlan is true, data was already saved when user adjusted hours
            if (!isRecoveryPlan && selectedPoints !== null && selectedHours !== null && userProgress?.shortcode) {
              try {
                // Calculate power and plays for the selected hours
                const savePower = calculatePower(selectedHours, optimalPaceNumber);
                const savePlaysData = calculatePlays(selectedHours, optimalPaceNumber);
                
                // Build save object based on recovery plan display mode
                const planData = { hoursPlan: selectedHours };
                if (recoveryPlanDisplayMode === 'plays') {
                  planData.playsPlan = savePlaysData.totalPlays;
                } else if (recoveryPlanDisplayMode === 'power') {
                  planData.powerPlan = savePower;
                } else {
                  planData.pointsPlan = selectedPoints;
                }
                
                await updateMostRecentJournalEntryPlan(userProgress.shortcode, planData);
              } catch (error) {
                console.error('Error saving plan data on continue:', error);
              }
            }
            onNext?.(data.key);
          }}>
            {ctaButtonConfig.text}
          </ContinueButton>
        )}
      </Content>
    </Container>
  );
};

export default OptimalPaceSoloScreen;

